import { Button, IconButton, Spinner } from "@design";
import { ImagePlus, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { formatBytes, formatNumber } from "@/i18n/format";
import { MAX_SCREENSHOT_BYTES, MAX_SCREENSHOTS, type Screenshot, type ScreenshotRejection } from "./feedbackForm";
import { type PageCaptureSlot, reservedPlaces } from "./pageCaptureRules";

/** A square preview of one screenshot; its object URL is revoked when it's removed or the dialog closes. */
function Thumbnail({
  file,
  alt = file.name,
  removeLabel,
  onRemove,
}: Readonly<{ file: File; alt?: string; removeLabel: string; onRemove: () => void }>) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return (
    <li className="relative size-16 shrink-0">
      {url && <img src={url} alt={alt} className="size-16 rounded border border-border-subtle object-cover" />}
      <span className="absolute -top-2 -right-2">
        <IconButton icon={<X />} aria-label={removeLabel} variant="secondaryBordered" size="xs" onClick={onRemove} />
      </span>
    </li>
  );
}

/** While the page is being captured: a placeholder in the thumbnail's place. */
function CapturingThumbnail() {
  const { t } = useTranslation("feedback");
  return (
    <li
      aria-busy="true"
      className="flex size-16 shrink-0 items-center justify-center rounded border border-dashed border-border-subtle bg-surface-secondary"
    >
      <Spinner size="sm" aria-label={t("pageCapture.capturing")} />
    </li>
  );
}

export interface PageCaptureView {
  slot: PageCaptureSlot;
  file: File | null;
  onRemove: () => void;
}

const REJECTION_MESSAGE = { type: "notImage", size: "tooLarge", count: "tooMany" } as const;
const PAGE_CAPTURE_NOTE = { capturing: "capturing", attached: "included", failed: "failed", none: null } as const;

/**
 * Optional screenshots: the page screenshot (SH-FB-08), then mine: pick with "Add screenshot" (or
 * paste, handled by the form), previews with remove, an n / max counter, and why a file was refused.
 */
export function ScreenshotsField({
  screenshots,
  pageCapture,
  rejected,
  onAdd,
  onRemove,
}: Readonly<{
  screenshots: readonly Screenshot[];
  pageCapture: PageCaptureView;
  rejected: ScreenshotRejection | null;
  onAdd: (files: File[]) => void;
  onRemove: (id: number) => void;
}>) {
  const { t } = useTranslation("feedback");
  const input = useRef<HTMLInputElement>(null);
  const labelId = useId();
  const size = formatBytes(MAX_SCREENSHOT_BYTES);
  const count = screenshots.length + reservedPlaces(pageCapture.slot);
  const full = count >= MAX_SCREENSHOTS;
  const note = PAGE_CAPTURE_NOTE[pageCapture.slot];
  return (
    <section aria-labelledby={labelId} className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <Text as="h3" id={labelId} variant="body-sm" weight="semibold">
          {t("screenshots.label")}
        </Text>
        <Text as="span" variant="caption" tone="tertiary">
          {t("screenshots.count", { count: formatNumber(count), max: formatNumber(MAX_SCREENSHOTS) })}
        </Text>
      </div>
      {count > 0 && (
        <ul className="flex flex-wrap gap-3 pt-2 pr-2">
          {pageCapture.slot === "capturing" && <CapturingThumbnail />}
          {pageCapture.slot === "attached" && pageCapture.file && (
            <Thumbnail
              file={pageCapture.file}
              alt={t("pageCapture.alt")}
              removeLabel={t("pageCapture.remove")}
              onRemove={pageCapture.onRemove}
            />
          )}
          {screenshots.map(({ id, file }) => (
            <Thumbnail
              key={id}
              file={file}
              removeLabel={t("screenshots.remove", { name: file.name })}
              onRemove={() => onRemove(id)}
            />
          ))}
        </ul>
      )}
      {/* Always rendered, so screen readers announce each change (capturing → included or failed). */}
      <Text role="status" variant="caption" tone="secondary" className="empty:hidden">
        {note && t(`pageCapture.${note}`)}
      </Text>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<ImagePlus size={16} aria-hidden />}
          disabled={full}
          onClick={() => input.current?.click()}
        >
          {t("screenshots.add")}
        </Button>
        <Text as="span" variant="caption" tone="tertiary">
          {t("screenshots.pasteHint")}
        </Text>
      </div>
      <Text variant="caption" tone="tertiary">
        {t("screenshots.hint", { size })}
      </Text>
      {rejected && (
        <Text role="alert" variant="body-sm" tone="error">
          {t(`screenshots.${REJECTION_MESSAGE[rejected]}`, { size, max: formatNumber(MAX_SCREENSHOTS) })}
        </Text>
      )}
      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        hidden
        aria-label={t("screenshots.add")}
        onChange={(event) => {
          onAdd(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
      />
    </section>
  );
}
